<template>
  <v-container fluid class="job-page">
    <AError :api="apiGetJob" />

    <!-- Alert Banner -->
    <AAlertBanner variant="danger" :messages="alertError" title="Warning" :icon-size="22" />

    <!-- Header -->
    <div class="job-header">
      <div class="job-header__top">
        <div class="job-header__info" v-if="!loadingDialog">
          <h1 class="job-header__title">{{ model.add.name }}</h1>
        </div>
        <div class="job-header__actions">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <button
                class="hero-action-btn hero-action-btn--blue"
                v-bind="attrs"
                v-on="on"
                @click="manuallyExecuteCron()"
              >
                <v-icon size="18">mdi-cog-play</v-icon>
              </button>
            </template>
            <span>Execute Cron Job</span>
          </v-tooltip>
        </div>
      </div>
    </div>

    <!-- Data Table Section -->
    <div class="section-card">
      <div class="section-header">
        <v-icon color="white" small>mdi-briefcase-clock-outline</v-icon>
        <span class="section-header__title">Job Execution History</span>
        <v-spacer></v-spacer>
        <span class="section-header__count">{{ jobDatas.length }} records</span>
      </div>
      <div class="section-body section-body--table">
        <v-data-table
          :headers="headers"
          :items="jobDatas"
          :items-per-page="10"
          class="job-table"
          :footer-props="{
            'items-per-page-options': [10, 25, 50],
          }"
        >
          <!-- Index column -->
          <template v-slot:item.index="{ item }">
            <span class="table-index">{{ item.index }}</span>
          </template>

          <!-- Description column -->
          <template v-slot:item.description="{ item }">
            <span class="table-description">{{ item.description }}</span>
          </template>

          <!-- Error column -->
          <template v-slot:item.err="{ item }">
            <div class="table-error" v-html="item.err"></div>
          </template>

          <!-- Execute Date column -->
          <template v-slot:item.timestampCreated="{ item }">
            <div class="table-date">
              <v-icon size="14" color="var(--app-text-faint)" class="mr-1">mdi-clock-outline</v-icon>
              <span>{{ item.timestampCreated }}</span>
            </div>
          </template>

          <!-- Actions column -->
          <template v-slot:item.actions="{ item }">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <button
                  class="table-action-btn"
                  v-bind="attrs"
                  v-on="on"
                  @click="revertAction(item)"
                >
                  <v-icon size="16" color="var(--app-text-muted)">mdi-undo</v-icon>
                </button>
              </template>
              <span>Revert</span>
            </v-tooltip>
          </template>

          <!-- Empty state -->
          <template v-slot:no-data>
            <div class="table-empty">
              <v-icon size="48" color="#cbd5e1">mdi-briefcase-off-outline</v-icon>
              <span class="table-empty__text">No job records found</span>
            </div>
          </template>
        </v-data-table>
      </div>
    </div>

    <HrdcLoader :loadingDialog="loadingDialog"></HrdcLoader>
  </v-container>
</template>

<script>
import moment from "moment";
import Api from "@/objects/api";
import { mapState } from "vuex";
import Model from "@/objects/model";
import Service from "@/objects/service";
import hrdcFunction from "@/objects/hrdcFunction";
import HrdcData from "@/services/hrdc/objects/globalData";

export default {
  mixins: [hrdcFunction],
  computed: {
    ...mapState({
      auth: (state) => state.auth.data,
    }),
  },
  props: ["params"],
  data: () => ({
    apiGetJob: new Api(),
    apiRevertJob: new Api(),
    apiManualExecJob: new Api(),
    model: new Model(),
    service: new Service(),
    hrdcData: new HrdcData(),
    alertError: [],
    errorField: {
      applicationNo: null,
    },
    loadingDialog: false,
    headers: [
      {
        text: "No.",
        align: "start",
        sortable: false,
        value: "index",
        width: "70px",
      },
      { text: "Description", value: "description", sortable: false },
      { text: "Execute Date", value: "timestampCreated", sortable: false, width: "200px" },
      { text: "Actions", value: "actions", sortable: false, align: "center", width: "100px" },
    ],
    jobDatas: [],
  }),
  async created() {
    this.showLoadingDialog();
    this.model.getByKey(this.params.modelKey);
    if (this.model.serviceKey) this.service.set("key", this.model.serviceKey);

    // Assign apiDetails by mapping repository.
    const apiDetails = {
      apiGetJob: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/settings/job/get-job`,
      },
      apiManualExecJob: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/application/check-expired-grants`,
      },
      apiRevertJob: {
        url: `${
          this.$service[this.service.key]
        }/v1/en/console/settings/job/revert-job`,
        method: "post",
      },
    };

    for (const api in apiDetails) {
      if (apiDetails[api].url !== undefined) {
        this[api].setUrl(apiDetails[api].url);
      }
      if (apiDetails[api].method !== undefined) {
        this[api].setMethod(apiDetails[api].method);
      }
      if (apiDetails[api].params !== undefined) {
        this[api].setParams(apiDetails[api].params);
      }
    }

    this.stages = await this.hrdcData.allStages;
    // No role gating in the developer console — the secret key is the authorization.
    this.jobDatas = [];

    this.apiGetJob.setCallbackCompleted((response) => {
      try {
        const { status, data } = response;

        if (!status) {
          this.$store.dispatch("showMessage", {
            message: data,
            messageType: "error",
            timeout: 2000,
          });

          return;
        }

        if (status) {
          if (data) {
            let index = 1;
            for (const item of data) {
              const { id, description, timestampCreated } = item;

              this.jobDatas.push({
                id,
                index,
                description,
                timestampCreated: moment(timestampCreated).format(
                  "YYYY-MM-DD HH:mm:ss"
                ),
              });
              index++;
            }
          }
        }
        this.hideLoadingDialog();
      } catch (err) {
        console.log(err);
      }
    });
    this.apiGetJob.fetch();

    this.hideLoadingDialog();
  },
  methods: {
    redirectBack() {
      this.$router.push({ name: "PageDeveloperSettings" }).catch(() => {});
    },
    manuallyExecuteCron() {
      this.showLoadingDialog();

      // Auth is handled by api.js, which attaches the developer-console session
      // token (this page is requiresDev). The backend authorizes the request via
      // fastify.guard.isHrdcSuperAdminOrDeveloper — no API key needed.
      this.apiManualExecJob.setCallbackCompleted(() => {
        this.jobDatas = [];
        this.apiGetJob.fetch();

        this.$store.dispatch("showMessage", {
          message: "Job Executed Successfully!",
          messageType: "success",
          timeout: 2000,
        });

        this.hideLoadingDialog();
      });

      this.apiManualExecJob.fetch();
    },
    revertAction(item) {
      this.showLoadingDialog();

      this.apiRevertJob.setParams({
        type: 1, // 1=DELETE EXPIRED CRON
        id: item.id,
      });

      this.apiRevertJob.setCallbackCompleted(() => {
        this.$store.dispatch("showMessage", {
          message: "Job Executed Successfully!",
          messageType: "success",
          timeout: 2000,
        });

        this.hideLoadingDialog();
      });

      this.apiRevertJob.fetch();
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .job-page {
  background-color: var(--app-page);
}

.dark-mode .job-header {
  background: var(--app-elevated);
}

.dark-mode .back-btn {
  background: var(--app-card);
  border: 1px solid var(--app-border-strong);
}

.dark-mode .back-btn:hover {
  background: var(--app-surface);
  border-color: var(--app-border-strong);
}

.dark-mode .hero-action-btn--blue {
  background: var(--app-accent-soft);
}

.dark-mode .section-card {
  background: var(--app-elevated);
}

.dark-mode .section-body {
  background: var(--app-card);
}

.dark-mode .job-table >>> .v-data-table-header {
  background: var(--app-surface);
}

.dark-mode .job-table >>> .v-data-table-header th {
  color: var(--app-text-muted) !important;
  border-bottom: 2px solid var(--app-border) !important;
}

.dark-mode .job-table >>> tbody tr:hover {
  background: var(--app-surface) !important;
}

.dark-mode .job-table >>> tbody td {
  border-bottom: 1px solid var(--app-border) !important;
  color: var(--app-text);
}

.dark-mode .job-table >>> .v-data-footer {
  border-top: 1px solid var(--app-border) !important;
  background: var(--app-surface);
}

.dark-mode .job-table >>> .v-data-footer__select {
  color: var(--app-text-muted) !important;
}

.dark-mode .job-table >>> .v-data-footer__select .v-input__slot {
  background: var(--app-card) !important;
  border: 1px solid var(--app-border) !important;
}

.dark-mode .job-table >>> .v-data-footer__pagination {
  color: var(--app-text-muted) !important;
}

.dark-mode .job-table >>> .v-data-footer__icons-before .v-btn,
.dark-mode .job-table >>> .v-data-footer__icons-after .v-btn {
  background: var(--app-card) !important;
  border: 1px solid var(--app-border) !important;
}

.dark-mode .job-table >>> .v-data-footer__icons-before .v-btn:hover:not(.v-btn--disabled),
.dark-mode .job-table >>> .v-data-footer__icons-after .v-btn:hover:not(.v-btn--disabled) {
  background: var(--app-accent-soft) !important;
}

.dark-mode .job-table >>> .v-data-footer__icons-before .v-btn .v-icon,
.dark-mode .job-table >>> .v-data-footer__icons-after .v-btn .v-icon {
  color: var(--app-text-muted) !important;
}

.dark-mode .table-index {
  background: var(--app-elevated);
  color: var(--app-text-muted);
}

.dark-mode .table-description {
  color: var(--app-text);
}

.dark-mode .table-date {
  color: var(--app-text);
}

.dark-mode .table-action-btn {
  border: 1px solid var(--app-border);
  background: var(--app-card);
}

.dark-mode .table-action-btn:hover {
  background: var(--app-elevated);
  border-color: var(--app-border-strong);
}

.dark-mode .table-empty__text {
  color: var(--app-text-faint);
}

/* >>> end auto-dark <<< */

/* ============================================
   PAGE WRAPPER
   ============================================ */
.job-page {
  background-color: #d8dce2;
  /* Fills main minus the breadcrumb and footer; a `100vh - 64px` guess only
     subtracts the app bar and overflows. See .layout-main__content. */
  flex: 1 0 auto;
  padding-top: 24px !important;
  padding-bottom: 60px !important;
  font-family: 'Lato', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
}

/* ============================================
   HEADER
   ============================================ */
.job-header {
  background: #eaecef;
  border-radius: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  margin-bottom: 24px;
  overflow: hidden;
}

.job-header__top {
  display: flex;
  align-items: center;
  padding: 24px 28px;
}

.back-btn {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid #d1d5db;
  cursor: pointer;
  margin-right: 16px;
  transition: all 0.2s ease;
}

.back-btn:hover {
  background: #f8fafc;
  border-color: #94a3b8;
}

.job-header__info {
  flex: 1;
  min-width: 0;
}

.job-header__title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #3a3f47;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  letter-spacing: -0.2px;
}

.job-header__actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

/* ============================================
   ACTION BUTTONS
   ============================================ */
.hero-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  border-radius: 12px;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
}

.hero-action-btn--blue {
  background: #dbeafe;
  color: #1d4ed8;
}

.hero-action-btn--blue:hover {
  background: #bfdbfe;
  color: #1e40af;
}

/* ============================================
   SECTION CARDS
   ============================================ */
.section-card {
  background: #eaecef;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.03);
  overflow: hidden;
  transition: box-shadow 0.3s ease;
  margin-bottom: 24px;
}

.section-card:hover {
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.06);
}

.section-header {
  background: linear-gradient(135deg, #0ea5e9, #38bdf8);
  padding: 13px 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.section-header__title {
  color: #ffffff;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
}

.section-header__count {
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.72rem;
  font-weight: 500;
}

.section-body {
  background: #fff;
}

.section-body--table {
  padding: 0;
}

/* ============================================
   DATA TABLE
   ============================================ */
.job-table {
  background: transparent !important;
  box-shadow: none !important;
}

.job-table >>> .v-data-table-header {
  background: #f8fafc;
}

.job-table >>> .v-data-table-header th {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.72rem !important;
  font-weight: 700 !important;
  color: #64748b !important;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  border-bottom: 2px solid #e2e8f0 !important;
  padding: 14px 16px !important;
  white-space: nowrap;
}

.job-table >>> tbody tr {
  transition: background 0.15s ease;
}

.job-table >>> tbody tr:hover {
  background: #f8fafc !important;
}

.job-table >>> tbody td {
  font-family: 'Lato', sans-serif !important;
  font-size: 0.85rem !important;
  font-weight: 900 !important;
  padding: 12px 16px !important;
  border-bottom: 1px solid #f1f5f9 !important;
  color: #0f172a;
}

.job-table >>> .v-data-footer {
  border-top: 1px solid #e2e8f0 !important;
  font-family: 'Lato', sans-serif !important;
  padding: 12px 20px !important;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
}

.job-table >>> .v-data-footer__select {
  font-size: 0.78rem !important;
  color: #64748b !important;
  font-weight: 600;
  margin-right: 16px !important;
}

.job-table >>> .v-data-footer__select .v-select {
  font-family: 'Lato', sans-serif !important;
  margin: 0 0 0 8px !important;
}

.job-table >>> .v-data-footer__select .v-input__slot {
  background: #fff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 8px !important;
  min-height: 32px !important;
  padding: 0 8px !important;
  box-shadow: none !important;
}

.job-table >>> .v-data-footer__select .v-input__slot::before,
.job-table >>> .v-data-footer__select .v-input__slot::after {
  display: none !important;
}

.job-table >>> .v-data-footer__pagination {
  font-size: 0.78rem !important;
  color: #64748b !important;
  font-weight: 600;
  margin: 0 8px !important;
}

.job-table >>> .v-data-footer__icons-before,
.job-table >>> .v-data-footer__icons-after {
  display: inline-flex;
  gap: 4px;
}

.job-table >>> .v-data-footer__icons-before .v-btn,
.job-table >>> .v-data-footer__icons-after .v-btn {
  width: 32px !important;
  height: 32px !important;
  background: #fff !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 8px !important;
  margin: 0 2px !important;
  transition: all 0.15s ease;
}

.job-table >>> .v-data-footer__icons-before .v-btn:hover:not(.v-btn--disabled),
.job-table >>> .v-data-footer__icons-after .v-btn:hover:not(.v-btn--disabled) {
  background: #e0f2fe !important;
  border-color: #0ea5e9 !important;
}

.job-table >>> .v-data-footer__icons-before .v-btn--disabled,
.job-table >>> .v-data-footer__icons-after .v-btn--disabled {
  opacity: 0.4 !important;
}

.job-table >>> .v-data-footer__icons-before .v-btn .v-icon,
.job-table >>> .v-data-footer__icons-after .v-btn .v-icon {
  color: #64748b !important;
  font-size: 18px !important;
}

/* Table cell styles */
.table-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 0.78rem;
  font-weight: 700;
}

.table-description {
  font-weight: 600;
  color: #1e293b;
}

.table-error {
  color: #dc2626;
  font-size: 0.82rem;
}

.table-date {
  display: inline-flex;
  align-items: center;
  font-size: 0.85rem;
  color: #0f172a;
  font-weight: 500;
}

.table-action-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}

.table-action-btn:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}

/* Empty state */
.table-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 48px 0;
}

.table-empty__text {
  font-size: 0.88rem;
  color: #94a3b8;
  font-weight: 500;
}

/* ============================================
   TRANSITIONS
   ============================================ */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* ============================================
   RESPONSIVE
   ============================================ */
@media (max-width: 959px) {
  .job-header__top {
    padding: 16px 20px;
  }
}

/* ============================================
   DARK MODE
   ============================================ */
.dark-mode .job-page { background-color: var(--app-page); }
.dark-mode .job-header { background: var(--app-card); }
.dark-mode .job-header__title { color: var(--app-text); }
.dark-mode .back-btn { background: var(--app-page); border-color: var(--app-border); }
.dark-mode .back-btn:hover { background: var(--app-card); }
.dark-mode .section-card { background: var(--app-card); }
.dark-mode .section-body { background: var(--app-card); }
.dark-mode .job-table >>> .v-data-table-header { background: var(--app-page); }
.dark-mode .job-table >>> tbody td { color: var(--app-text) !important; border-color: var(--app-border) !important; }
.dark-mode .job-table >>> tbody tr:hover { background: var(--app-border) !important; }
.dark-mode .job-table >>> .v-data-footer { background: var(--app-page); border-color: var(--app-border) !important; }
.dark-mode .table-index { background: var(--app-border); color: var(--app-text-muted); }
.dark-mode .table-description { color: var(--app-text); }
.dark-mode .table-date { color: var(--app-text); }
.dark-mode .table-action-btn { background: var(--app-card); border-color: var(--app-border); }
.dark-mode .table-action-btn:hover { background: var(--app-border); }
</style>

<style>
/* The unscoped `.v-menu__content …` block that used to sit here is gone.
   It was global — unscoped CSS is injected once and never removed, so a rule
   written for THIS page's dropdown repainted every menu in the app (with
   !important, and with hard-coded light hex) for the rest of the session. That
   is what made the same v-select look different depending on which page you had
   visited. Menus are defined ONCE, in tokens, in src/plugins/theme-glass.css §3
   — panel, list, item radius, hover, active, typography and the state overlay.
   A page that genuinely needs its own menu styling should give that v-menu a
   `content-class` and key off it, the way .bdt-menu and .tb-view-menu do. */
</style>
